import { useState, useEffect, useMemo } from 'react'
import { useUserSettings } from '@/hooks/settings'
import moment from 'moment'
import { endOfDay } from 'date-fns'

// Helper to get default date range for All Open and other tabs (30 days for consistency with trends)
function getDefaultDateRange(): [Date, Date] {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const start = new Date(today)
  start.setDate(today.getDate() - 30) // Exactly 30 days back

  const end = new Date(today)
  end.setDate(today.getDate() + 1) // Exactly 1 day ahead

  return [start, endOfDay(end)]
}

// Helper to get default date range for Approved view (7 days per SR-92)
function getDefaultApprovedDateRange(): [Date, Date] {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const start = new Date(today)
  start.setDate(today.getDate() - 7) // Exactly 7 days back

  const end = new Date(today)
  end.setDate(today.getDate() + 1) // Exactly 1 day ahead

  return [start, endOfDay(end)]
}

// Helper to get default date range for Voided view (90 days per SR-435)
function getDefaultVoidedDateRange(): [Date, Date] {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const start = new Date(today)
  start.setDate(today.getDate() - 90) // Exactly 90 days back

  const end = new Date(today)
  end.setDate(today.getDate() + 1) // Exactly 1 day ahead

  return [start, endOfDay(end)]
}

// Helper to get default dayRangeStorage values for each tab
function getDefaultApprovedDayRangeStorage(): { back: number; ahead: number } {
  return { back: -7, ahead: 1 }
}

function getDefaultVoidedDayRangeStorage(): { back: number; ahead: number } {
  return { back: -90, ahead: 1 }
}

function getDefaultOtherTabsDayRangeStorage(): { back: number; ahead: number } {
  return { back: -30, ahead: 1 }
}

// Helper to calculate dayRangeStorage from actual date range
function calculateDayRangeFromDates(startDate: Date, endDate: Date): { back: number; ahead: number } {
  // Normalize all dates to start of day for consistent calculations
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const startNormalized = new Date(startDate)
  startNormalized.setHours(0, 0, 0, 0)

  const endNormalized = new Date(endDate)
  endNormalized.setHours(0, 0, 0, 0)

  // Calculate differences in days
  const startDiff = Math.round((startNormalized.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  const endDiff = Math.round((endNormalized.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

  return {
    back: startDiff, // This will be negative for past dates
    ahead: endDiff, // This will be positive for future dates
  }
}

// Date serialization utilities for persistent storage
function serializeDateRange(range: [Date?, Date?]): [string?, string?] {
  return [range[0]?.toISOString(), range[1]?.toISOString()]
}

function deserializeDateRange(range: [string?, string?] | null): [Date?, Date?] {
  if (!range) return [undefined, undefined]
  return [range[0] ? new Date(range[0]) : undefined, range[1] ? new Date(range[1]) : undefined]
}

// Interface for storage hook parameters
interface UseMatchStorageParams {
  isVoidedViewActive: boolean
  activeStatus: string | null
  hasUrlDateRange?: boolean // Prevent overriding URL parameters
}

// Interface for storage hook return value
export interface UseMatchStorageReturn {
  // Date Range State
  approvedDateRange: [Date?, Date?]
  voidedDateRange: [Date?, Date?]
  otherTabsDateRange: [Date?, Date?]
  dateRange: [Date?, Date?]
  dateRangesLoaded: boolean

  // Date Range Setters
  setApprovedDateRange: (range: [Date?, Date?]) => void
  setVoidedDateRange: (range: [Date?, Date?]) => void
  setOtherTabsDateRange: (range: [Date?, Date?]) => void
  setDateRange: (range: [Date?, Date?]) => void

  // DateRangeFilter State
  approvedDateFilters: { From: moment.Moment; To: moment.Moment }
  voidedDateFilters: { From: moment.Moment; To: moment.Moment }
  otherTabsDateFilters: { From: moment.Moment; To: moment.Moment }
  dateFilters: { From: moment.Moment; To: moment.Moment }

  // DateRangeFilter Setters
  setApprovedDateFilters: (filters: { From: moment.Moment; To: moment.Moment }) => void
  setVoidedDateFilters: (filters: { From: moment.Moment; To: moment.Moment }) => void
  setOtherTabsDateFilters: (filters: { From: moment.Moment; To: moment.Moment }) => void
  setDateFilters: (filters: { From: moment.Moment; To: moment.Moment }) => void

  // Day Range Storage State
  approvedDayRangeStorage: { back: number; ahead: number }
  voidedDayRangeStorage: { back: number; ahead: number }
  otherTabsDayRangeStorage: { back: number; ahead: number }
  dayRangeStorage: { back: number; ahead: number }

  // Day Range Storage Setters
  setApprovedDayRangeStorage: (range: { back: number; ahead: number }) => void
  setVoidedDayRangeStorage: (range: { back: number; ahead: number }) => void
  setOtherTabsDayRangeStorage: (range: { back: number; ahead: number }) => void
  setDayRangeStorage: (range: { back: number; ahead: number }) => void

  // Default Helper Functions
  getDefaultApprovedDayRangeStorage: () => { back: number; ahead: number }
  getDefaultVoidedDayRangeStorage: () => { back: number; ahead: number }
  getDefaultOtherTabsDayRangeStorage: () => { back: number; ahead: number }
  getDefaultApprovedDateRange: () => [Date, Date]
  getDefaultVoidedDateRange: () => [Date, Date]
  getDefaultDateRange: () => [Date, Date]
}

export function useMatchStorage({
  isVoidedViewActive,
  activeStatus,
  hasUrlDateRange = false,
}: UseMatchStorageParams): UseMatchStorageReturn {
  const { getSetting, updateSetting } = useUserSettings()

  // TEMPORARY: Disable auto-saving for testing defaults
  const DISABLE_AUTO_SAVE = false // Set to true to test defaults

  // Date Range State - separate state for Approved, Voided, and other tabs per SR-92, SR-435
  // Using persistent storage for date ranges - delay API calls until settings are loaded
  const [approvedDateRange, setApprovedDateRangeState] = useState<[Date?, Date?]>([undefined, undefined])
  const [voidedDateRange, setVoidedDateRangeState] = useState<[Date?, Date?]>([undefined, undefined])
  const [otherTabsDateRange, setOtherTabsDateRangeState] = useState<[Date?, Date?]>([undefined, undefined])
  const [dateRangesLoaded, setDateRangesLoaded] = useState<boolean>(false)

  // Current active date range based on tab or voided view
  const dateRange = isVoidedViewActive
    ? voidedDateRange
    : activeStatus === 'approved'
      ? approvedDateRange
      : otherTabsDateRange

  // Persistent wrapper functions for date ranges
  const setApprovedDateRange = (range: [Date?, Date?]) => {
    setApprovedDateRangeState(range)
    if (!DISABLE_AUTO_SAVE) {
      updateSetting('dateRange::match-view-approved', serializeDateRange(range))
    }
  }

  const setVoidedDateRange = (range: [Date?, Date?]) => {
    setVoidedDateRangeState(range)
    if (!DISABLE_AUTO_SAVE) {
      updateSetting('dateRange::match-view-voided', serializeDateRange(range))
    }
  }

  const setOtherTabsDateRange = (range: [Date?, Date?]) => {
    setOtherTabsDateRangeState(range)
    if (!DISABLE_AUTO_SAVE) {
      updateSetting('dateRange::match-view-other-tabs', serializeDateRange(range))
    }
  }

  // Persistent wrapper functions for dayRangeStorage that also update corresponding date ranges
  const setApprovedDayRangeStoragePersistent = (range: { back: number; ahead: number }) => {
    setApprovedDayRangeStorage(range)

    // Calculate corresponding date range from slider values
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const startDate = new Date(today)
    startDate.setDate(today.getDate() + range.back) // range.back is negative

    const endDate = new Date(today)
    endDate.setDate(today.getDate() + range.ahead)

    // Update the corresponding date range with endOfDay for end date
    setApprovedDateRange([startDate, endOfDay(endDate)])

    if (!DISABLE_AUTO_SAVE) {
      updateSetting('dayRangeStorage::match-view-approved', range)
    }
  }

  const setVoidedDayRangeStoragePersistent = (range: { back: number; ahead: number }) => {
    setVoidedDayRangeStorage(range)

    // Calculate corresponding date range from slider values
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const startDate = new Date(today)
    startDate.setDate(today.getDate() + range.back) // range.back is negative

    const endDate = new Date(today)
    endDate.setDate(today.getDate() + range.ahead)

    // Update the corresponding date range with endOfDay for end date
    setVoidedDateRange([startDate, endOfDay(endDate)])

    if (!DISABLE_AUTO_SAVE) {
      updateSetting('dayRangeStorage::match-view-voided', range)
    }
  }

  const setOtherTabsDayRangeStoragePersistent = (range: { back: number; ahead: number }) => {
    setOtherTabsDayRangeStorage(range)

    // Calculate corresponding date range from slider values
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const startDate = new Date(today)
    startDate.setDate(today.getDate() + range.back) // range.back is negative

    const endDate = new Date(today)
    endDate.setDate(today.getDate() + range.ahead)

    // Update the corresponding date range with endOfDay for end date
    setOtherTabsDateRange([startDate, endOfDay(endDate)])

    if (!DISABLE_AUTO_SAVE) {
      updateSetting('dayRangeStorage::match-view-other-tabs', range)
    }
  }

  const setDateRange = isVoidedViewActive
    ? setVoidedDateRange
    : activeStatus === 'approved'
      ? setApprovedDateRange
      : setOtherTabsDateRange

  // --- DateRangeFilter State --- //
  // Separate DateRangeFilter states for each tab to maintain independent settings
  // Initialize with placeholder values - will be updated from actual date ranges once loaded

  // Approved tab DateRangeFilter state
  const [approvedDateFilters, setApprovedDateFilters] = useState<{ From: moment.Moment; To: moment.Moment }>(() => {
    const [defaultStart, defaultEnd] = getDefaultApprovedDateRange()
    return {
      From: moment(defaultStart),
      To: moment(defaultEnd),
    }
  })

  const [approvedDayRangeStorage, setApprovedDayRangeStorage] = useState<{ back: number; ahead: number }>(() =>
    getDefaultApprovedDayRangeStorage()
  )

  // Voided tab DateRangeFilter state
  const [voidedDateFilters, setVoidedDateFilters] = useState<{ From: moment.Moment; To: moment.Moment }>(() => {
    const [defaultStart, defaultEnd] = getDefaultVoidedDateRange()
    return {
      From: moment(defaultStart),
      To: moment(defaultEnd),
    }
  })

  const [voidedDayRangeStorage, setVoidedDayRangeStorage] = useState<{ back: number; ahead: number }>(() =>
    getDefaultVoidedDayRangeStorage()
  )

  // Other tabs DateRangeFilter state
  const [otherTabsDateFilters, setOtherTabsDateFilters] = useState<{ From: moment.Moment; To: moment.Moment }>(() => {
    const [defaultStart, defaultEnd] = getDefaultDateRange()
    return {
      From: moment(defaultStart),
      To: moment(defaultEnd),
    }
  })

  const [otherTabsDayRangeStorage, setOtherTabsDayRangeStorage] = useState<{ back: number; ahead: number }>(() =>
    getDefaultOtherTabsDayRangeStorage()
  )

  // Simple computed values for current tab state
  const dateFilters = isVoidedViewActive
    ? voidedDateFilters
    : activeStatus === 'approved'
      ? approvedDateFilters
      : otherTabsDateFilters

  // Wrapper functions for setDateFilters that also sync dayRangeStorage
  const setApprovedDateFiltersWithSync = (filters: { From: moment.Moment; To: moment.Moment }) => {
    setApprovedDateFilters(filters)
    // Calculate and sync dayRangeStorage from calendar selection
    const dayRange = calculateDayRangeFromDates(filters.From.toDate(), filters.To.toDate())
    setApprovedDayRangeStoragePersistent(dayRange)
  }

  const setVoidedDateFiltersWithSync = (filters: { From: moment.Moment; To: moment.Moment }) => {
    setVoidedDateFilters(filters)
    // Calculate and sync dayRangeStorage from calendar selection
    const dayRange = calculateDayRangeFromDates(filters.From.toDate(), filters.To.toDate())
    setVoidedDayRangeStoragePersistent(dayRange)
  }

  const setOtherTabsDateFiltersWithSync = (filters: { From: moment.Moment; To: moment.Moment }) => {
    setOtherTabsDateFilters(filters)
    // Calculate and sync dayRangeStorage from calendar selection
    const dayRange = calculateDayRangeFromDates(filters.From.toDate(), filters.To.toDate())
    setOtherTabsDayRangeStoragePersistent(dayRange)
  }

  const setDateFilters = isVoidedViewActive
    ? setVoidedDateFiltersWithSync
    : activeStatus === 'approved'
      ? setApprovedDateFiltersWithSync
      : setOtherTabsDateFiltersWithSync

  const dayRangeStorage = isVoidedViewActive
    ? voidedDayRangeStorage
    : activeStatus === 'approved'
      ? approvedDayRangeStorage
      : otherTabsDayRangeStorage

  const setDayRangeStorage = isVoidedViewActive
    ? setVoidedDayRangeStoragePersistent
    : activeStatus === 'approved'
      ? setApprovedDayRangeStoragePersistent
      : setOtherTabsDayRangeStoragePersistent

  // Load stored date ranges from persistent storage on mount
  // Use parallel loading and single batch update to prevent multiple re-renders
  useEffect(() => {
    let isMounted = true

    const loadStoredDateRanges = async () => {
      // Skip loading stored ranges if URL parameters should take precedence
      if (hasUrlDateRange) {
        setDateRangesLoaded(true)
        return
      }

      try {
        // Load all date range settings and dayRangeStorage settings in parallel
        const [
          storedApproved,
          storedVoided,
          storedOtherTabs,
          storedApprovedDayRange,
          storedVoidedDayRange,
          storedOtherTabsDayRange,
        ] = await Promise.all([
          getSetting('dateRange::match-view-approved'),
          getSetting('dateRange::match-view-voided'),
          getSetting('dateRange::match-view-other-tabs'),
          getSetting('dayRangeStorage::match-view-approved'),
          getSetting('dayRangeStorage::match-view-voided'),
          getSetting('dayRangeStorage::match-view-other-tabs'),
        ])

        if (!isMounted) return

        // Initialize each tab: prioritize stored dayRangeStorage, then stored dates, then defaults
        // This ensures slider and date ranges are always synchronized

        // --- Approved Tab ---
        if (storedApprovedDayRange) {
          // Use stored slider values and generate date range from them
          setApprovedDayRangeStorage(storedApprovedDayRange)

          const today = new Date()
          today.setHours(0, 0, 0, 0)
          const startDate = new Date(today)
          startDate.setDate(today.getDate() + storedApprovedDayRange.back)
          const endDate = new Date(today)
          endDate.setDate(today.getDate() + storedApprovedDayRange.ahead)

          setApprovedDateRangeState([startDate, endOfDay(endDate)])
          setApprovedDateFilters({
            From: moment(startDate),
            To: moment(endOfDay(endDate)),
          })
        } else if (storedApproved) {
          // Use stored date range and calculate slider values
          const approvedRange = deserializeDateRange(storedApproved)
          setApprovedDateRangeState(approvedRange)
          setApprovedDateFilters({
            From: moment(approvedRange[0]),
            To: moment(approvedRange[1]),
          })
          setApprovedDayRangeStorage(calculateDayRangeFromDates(approvedRange[0]!, approvedRange[1]!))
        } else {
          // Use defaults
          const approvedDefault = getDefaultApprovedDateRange()
          setApprovedDateRangeState(approvedDefault)
          setApprovedDateFilters({
            From: moment(approvedDefault[0]),
            To: moment(approvedDefault[1]),
          })
          setApprovedDayRangeStorage(getDefaultApprovedDayRangeStorage())
        }

        // --- Voided Tab ---
        if (storedVoidedDayRange) {
          // Use stored slider values and generate date range from them
          setVoidedDayRangeStorage(storedVoidedDayRange)

          const today = new Date()
          today.setHours(0, 0, 0, 0)
          const startDate = new Date(today)
          startDate.setDate(today.getDate() + storedVoidedDayRange.back)
          const endDate = new Date(today)
          endDate.setDate(today.getDate() + storedVoidedDayRange.ahead)

          setVoidedDateRangeState([startDate, endOfDay(endDate)])
          setVoidedDateFilters({
            From: moment(startDate),
            To: moment(endOfDay(endDate)),
          })
        } else if (storedVoided) {
          // Use stored date range and calculate slider values
          const voidedRange = deserializeDateRange(storedVoided)
          setVoidedDateRangeState(voidedRange)
          setVoidedDateFilters({
            From: moment(voidedRange[0]),
            To: moment(voidedRange[1]),
          })
          setVoidedDayRangeStorage(calculateDayRangeFromDates(voidedRange[0]!, voidedRange[1]!))
        } else {
          // Use defaults
          const voidedDefault = getDefaultVoidedDateRange()
          setVoidedDateRangeState(voidedDefault)
          setVoidedDateFilters({
            From: moment(voidedDefault[0]),
            To: moment(voidedDefault[1]),
          })
          setVoidedDayRangeStorage(getDefaultVoidedDayRangeStorage())
        }

        // --- Other Tabs ---
        if (storedOtherTabsDayRange) {
          // Use stored slider values and generate date range from them
          setOtherTabsDayRangeStorage(storedOtherTabsDayRange)

          const today = new Date()
          today.setHours(0, 0, 0, 0)
          const startDate = new Date(today)
          startDate.setDate(today.getDate() + storedOtherTabsDayRange.back)
          const endDate = new Date(today)
          endDate.setDate(today.getDate() + storedOtherTabsDayRange.ahead)

          setOtherTabsDateRangeState([startDate, endOfDay(endDate)])
          setOtherTabsDateFilters({
            From: moment(startDate),
            To: moment(endOfDay(endDate)),
          })
        } else if (storedOtherTabs) {
          // Use stored date range and calculate slider values
          const otherTabsRange = deserializeDateRange(storedOtherTabs)
          setOtherTabsDateRangeState(otherTabsRange)
          setOtherTabsDateFilters({
            From: moment(otherTabsRange[0]),
            To: moment(otherTabsRange[1]),
          })
          setOtherTabsDayRangeStorage(calculateDayRangeFromDates(otherTabsRange[0]!, otherTabsRange[1]!))
        } else {
          // Use defaults
          const otherTabsDefault = getDefaultDateRange()
          setOtherTabsDateRangeState(otherTabsDefault)
          setOtherTabsDateFilters({
            From: moment(otherTabsDefault[0]),
            To: moment(otherTabsDefault[1]),
          })
          setOtherTabsDayRangeStorage(getDefaultOtherTabsDayRangeStorage())
        }

        // Mark date ranges as loaded to enable API calls
        setDateRangesLoaded(true)
      } catch (error) {
        console.error('Error loading stored date ranges:', error)
        // On error, use defaults and enable API calls
        const approvedDefault = getDefaultApprovedDateRange()
        const voidedDefault = getDefaultVoidedDateRange()
        const otherTabsDefault = getDefaultDateRange()

        setApprovedDateRangeState(approvedDefault)
        setVoidedDateRangeState(voidedDefault)
        setOtherTabsDateRangeState(otherTabsDefault)

        // Also sync DateRangeFilter states with defaults (no stored values available on error)
        setApprovedDateFilters({
          From: moment(approvedDefault[0]),
          To: moment(approvedDefault[1]),
        })
        setApprovedDayRangeStorage(getDefaultApprovedDayRangeStorage())

        setVoidedDateFilters({
          From: moment(voidedDefault[0]),
          To: moment(voidedDefault[1]),
        })
        setVoidedDayRangeStorage(getDefaultVoidedDayRangeStorage())

        setOtherTabsDateFilters({
          From: moment(otherTabsDefault[0]),
          To: moment(otherTabsDefault[1]),
        })
        setOtherTabsDayRangeStorage(getDefaultOtherTabsDayRangeStorage())

        setDateRangesLoaded(true)
      }
    }

    loadStoredDateRanges()

    return () => {
      isMounted = false
    }
  }, []) // Empty dependency array - load settings only once on mount

  return {
    // Date Range State
    approvedDateRange,
    voidedDateRange,
    otherTabsDateRange,
    dateRange,
    dateRangesLoaded,

    // Date Range Setters
    setApprovedDateRange,
    setVoidedDateRange,
    setOtherTabsDateRange,
    setDateRange,

    // DateRangeFilter State
    approvedDateFilters,
    voidedDateFilters,
    otherTabsDateFilters,
    dateFilters,

    // DateRangeFilter Setters
    setApprovedDateFilters,
    setVoidedDateFilters,
    setOtherTabsDateFilters,
    setDateFilters,

    // Day Range Storage State
    approvedDayRangeStorage,
    voidedDayRangeStorage,
    otherTabsDayRangeStorage,
    dayRangeStorage,

    // Day Range Storage Setters
    setApprovedDayRangeStorage,
    setVoidedDayRangeStorage,
    setOtherTabsDayRangeStorage,
    setDayRangeStorage,

    // Default Helper Functions
    getDefaultApprovedDayRangeStorage,
    getDefaultVoidedDayRangeStorage,
    getDefaultOtherTabsDayRangeStorage,
    getDefaultApprovedDateRange,
    getDefaultVoidedDateRange,
    getDefaultDateRange,
  }
}
